import { ElementState, ElementType } from '@ritabygglov/core';
import type { Element } from '@ritabygglov/core';
import { MouseEventHandler, ReactNode } from 'react';
import {
  computeGroupBBox,
  findContainingGroupId,
  resolveSelectionId,
} from '../../helpers/groupHelpers';
import { copy, MouseButtons, uuid } from '../../helpers/utility';
import { useAppState as useAppStateImport } from '../AppState/useAppState';

import { UpdateCommand } from '../../commands/UpdateCommand';
import { SelectionModes } from '../../types';
import { IAppStateContext } from '../AppState/AppStateContext';
import { useKeyboardEvents as useKeyboardEventsImport } from '../KeyboardEvents/useKeyboardEvents';
import { getClientCoordinates, getClosestElementId } from './helpers';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import { useApexDragging } from './hooks/useApexDragging';
import { useDimensionDragging } from './hooks/useDimensionDragging';
import { useRadiusDragging } from './hooks/useRadiusDragging';
import { useSplitSegment } from './hooks/useSplitSegment';
import { useElementCreation } from './hooks/useElementCreation';
import {
  createSelectionMode,
  useElementSelection,
} from './hooks/useElementSelection';
import { useElementTransformation } from './hooks/useElementTransformation';
import { MouseEventsContext } from './MouseEventsContext';
import {
  isApexHandle,
  isContainerElement,
  isDimensionHandle,
  isEditTextHandle,
  isMultiselectBody,
  isRadiusHandle,
  isResizeHandle,
  isRotateHandle,
  isSplitHandle,
} from './utils/typeGuards';

/**
 * Command Manager is to execute commands that can be undone/redone.
 * The Command Manager should be used in here, and helper functions should
 * prepare the changes. But then the execution of the command should be done
 * from MouseEvents. So each command starts and ends in MouseEvents.
 *
 */

export const MouseEventsProvider = ({
  children,
  useAppState = useAppStateImport,
  generateUuid = () => uuid(),
  useKeyboardEvents = useKeyboardEventsImport,
}: {
  children: ReactNode;
  useAppState?: () => IAppStateContext;
  generateUuid?: () => string;
  useKeyboardEvents?: () => void;
}) => {
  const hooks: IAppStateContext = useAppState();
  const {
    appState,
    session,
    selectedElements,
    setHoverElement,
    selectionMode,
    setSelectionMode,
    selectionCoordinates,
    viewBox,
    isSpacePressed,
    isShiftPressed,
    isMouseDown,
    setIsMouseDown,
    setCursorPosition,
    commandManager,
  } = hooks;

  useKeyboardEvents();

  const { onStartAddElement, onUpdateAddElement, onAddFinishElement } =
    useElementCreation(hooks);
  const {
    onStartResizing,
    onResizingElement,
    onStartRotating,
    onRotating,
    onSelected,
    onStartTextEditing,
    setupMoving,
    clearSnapping,
    onStopSelection,
  } = useElementTransformation(hooks);
  const {
    onStartPanning,
    onPanning,
    onStartMultiSelecting,
    onMultiSelecting,
    resetSelection,
    resetMultiSelection,
  } = useCanvasInteraction(hooks);

  const { onElementClick } = useElementSelection(hooks);
  const { onStartDimensionDrag, onDimensionDragging, onStopDimensionDrag } =
    useDimensionDragging(hooks);
  const { onStartApexDrag, onApexDragging, onStopApexDrag } =
    useApexDragging(hooks);
  const { onStartRadiusDrag, onRadiusDragging, onStopRadiusDrag } =
    useRadiusDragging(hooks);
  const { onStartSplitDrag } = useSplitSegment(hooks);

  const onMouseOver: MouseEventHandler<SVGSVGElement> = (e) => {
    if (!(e.target instanceof Element) || isContainerElement(e.target.id)) {
      setHoverElement(null);
      return;
    }
    const activeGroupId =
      selectionMode.type === SelectionModes.GroupEditing
        ? selectionMode.groupId
        : undefined;
    setHoverElement(
      resolveSelectionId(e.target.id, appState.elements, activeGroupId)
    );
  };

  const onMouseDown: MouseEventHandler<SVGSVGElement> = (e) => {
    setIsMouseDown(true);
    const clientCoordinates = getClientCoordinates(e, viewBox);

    if (e.button === MouseButtons.MIDDLE) {
      e.preventDefault();
      const { movementX, movementY } = e;
      onStartPanning(movementX, movementY);
    }
    if (e.button !== MouseButtons.LEFT) return;

    // Body of the None/Updating case, factored out so GroupEditing can
    // re-dispatch a click as a fresh "from None" click after exiting.
    const handleNoneModeClick = () => {
      if (!(e.target instanceof Element)) return;
      const isOnContainer = isContainerElement(e.target.id);
      const isPanning = isOnContainer && isSpacePressed;
      const isMultiSelecting = isOnContainer && !isSpacePressed;

      if (isPanning) {
        const { movementX, movementY } = e;
        onStartPanning(movementX, movementY);
        return;
      }
      if (isMultiSelecting) {
        onStartMultiSelecting(clientCoordinates);
        return;
      }
      const id = getClosestElementId(e);

      if (!id) {
        resetSelection();
        return;
      }

      if (isApexHandle(id)) {
        onStartApexDrag(clientCoordinates, id);
        return;
      } else if (isRadiusHandle(id)) {
        onStartRadiusDrag(clientCoordinates, id);
        return;
      } else if (isSplitHandle(id)) {
        onStartSplitDrag(id);
        return;
      } else if (isResizeHandle(id)) {
        onStartResizing(clientCoordinates, e, id);
        return;
      } else if (isRotateHandle(id)) {
        onStartRotating(e, id);
        return;
      } else if (isDimensionHandle(id)) {
        onStartDimensionDrag(clientCoordinates, id);
        return;
      } else if (isEditTextHandle(id)) {
        const elementId = id.split('-edit-text')[0];
        const element = appState.elements[elementId];
        if (element) {
          onStartTextEditing(element);
          setIsMouseDown(false);
        }
        return;
      } else if (isMultiselectBody(id)) {
        // selectionMode reverts to None after any completed click/move (see
        // onMouseUp) while selectedElements stays populated, so a *second*,
        // separate click+drag on the group's bbox interior arrives here
        // (not in the Selected case below) — this is a synthetic id, not a
        // real element, so it can't go through onElementClick's resolution.
        const elements = selectedElements
          .map((elId) => appState.elements[elId])
          .filter((el): el is Element => el !== undefined);
        if (elements.length === 0) {
          resetSelection();
          return;
        }
        // onElementClick would normally both resolve the id AND flip mode
        // to Selected — since there's no id to resolve here, that mode
        // transition has to happen explicitly, or onMouseMove's switch (no
        // case for None) will never call onSelected during the drag.
        setSelectionMode(createSelectionMode(elements[0], selectionMode));
        setupMoving(e, elements);
        return;
      }

      const elements = onElementClick(id, isShiftPressed);
      if (!elements) {
        resetSelection();
        return;
      }
      setupMoving(e, elements);
    };

    // Recomputes and saves the group's bbox if it changed, then leaves
    // GroupEditing (selection resets to None so handleNoneModeClick's
    // re-dispatch below starts clean).
    const exitGroupEditing = (groupId: string) => {
      const group = appState.elements[groupId];
      if (group && group.type === ElementType.Group) {
        const newBbox = computeGroupBBox(group, appState.elements);
        if (
          newBbox &&
          (newBbox.x !== group.x ||
            newBbox.y !== group.y ||
            newBbox.width !== group.width ||
            newBbox.height !== group.height)
        ) {
          const updatedGroup = { ...group, ...newBbox };
          const command = new UpdateCommand(
            [updatedGroup],
            [group],
            session.currentUserId,
            false
          );
          commandManager.executeCommand(command);
        }
      }
      setSelectionMode({ type: SelectionModes.None });
    };

    switch (selectionMode.type) {
      case SelectionModes.Updating:
      case SelectionModes.None: {
        handleNoneModeClick();
        break;
      }
      case SelectionModes.Add: {
        onStartAddElement(clientCoordinates, generateUuid);
        break;
      }
      case SelectionModes.MultiSelecting:
      case SelectionModes.TextEditing:
      case SelectionModes.Selected: {
        if (!(e.target instanceof Element) || isContainerElement(e.target.id)) {
          resetSelection();
          return;
        }
        if (isApexHandle(e.target.id)) {
          onStartApexDrag(clientCoordinates, e.target.id);
          break;
        } else if (isRadiusHandle(e.target.id)) {
          onStartRadiusDrag(clientCoordinates, e.target.id);
          break;
        } else if (isSplitHandle(e.target.id)) {
          onStartSplitDrag(e.target.id);
          break;
        } else if (isResizeHandle(e.target.id)) {
          onStartResizing(clientCoordinates, e, e.target.id);
          break;
        } else if (isRotateHandle(e.target.id)) {
          onStartRotating(e, e.target.id);
          break;
        } else if (isDimensionHandle(e.target.id)) {
          onStartDimensionDrag(clientCoordinates, e.target.id);
          break;
        } else if (isEditTextHandle(e.target.id)) {
          const elementId = e.target.id.split('-edit-text')[0];
          const element = appState.elements[elementId];
          if (element) {
            onStartTextEditing(element);
            setIsMouseDown(false);
          }
          break;
        } else if (isMultiselectBody(e.target.id)) {
          // Clicking anywhere inside an already-selected group/multiselect's
          // bbox (not just on a member shape's own edge) starts a move of
          // the current selection — this is a synthetic id, not a real
          // element, so it can't go through onElementClick's resolution.
          const elements = selectedElements
            .map((elId) => appState.elements[elId])
            .filter((el): el is Element => el !== undefined);
          if (elements.length === 0) {
            resetSelection();
            break;
          }
          // A rubber-band selection leaves selectionMode at MultiSelecting
          // (resetMultiSelection on mouseup only clears the drag rect, see
          // useCanvasInteraction) — onElementClick would normally flip mode
          // to Selected on a real-element click, but there's no id to
          // resolve here. Without this, onMouseMove's switch still matches
          // MultiSelecting and calls onMultiSelecting (a fresh rubber-band)
          // instead of onSelected (the move), so nothing moves and a new
          // selection rectangle starts instead.
          setSelectionMode(createSelectionMode(elements[0], selectionMode));
          setupMoving(e, elements.map(copy));
          break;
        }

        const id = getClosestElementId(e) || e.target.id;
        const elements = onElementClick(id, isShiftPressed);
        if (!elements) {
          resetSelection();
          break;
        }
        setupMoving(e, elements.map(copy));
        break;
      }
      case SelectionModes.GroupEditing: {
        const activeGroupId = selectionMode.groupId;

        if (!(e.target instanceof Element) || isContainerElement(e.target.id)) {
          if (activeGroupId) exitGroupEditing(activeGroupId);
          handleNoneModeClick();
          return;
        }

        const rawId = getClosestElementId(e) || e.target.id;
        const isHandle =
          isApexHandle(rawId) ||
          isRadiusHandle(rawId) ||
          isSplitHandle(rawId) ||
          isResizeHandle(rawId) ||
          isRotateHandle(rawId) ||
          isDimensionHandle(rawId) ||
          isEditTextHandle(rawId);

        const belongsToActiveGroup =
          activeGroupId !== undefined &&
          findContainingGroupId(rawId, appState.elements) === activeGroupId;

        // Handles (resize/rotate/apex/etc.) only ever render for elements
        // already scoped to the open group, so they're always in-bounds.
        if (isHandle || belongsToActiveGroup || rawId === activeGroupId) {
          if (isApexHandle(rawId)) {
            onStartApexDrag(clientCoordinates, rawId);
            break;
          } else if (isRadiusHandle(rawId)) {
            onStartRadiusDrag(clientCoordinates, rawId);
            break;
          } else if (isSplitHandle(rawId)) {
            onStartSplitDrag(rawId);
            break;
          } else if (isResizeHandle(rawId)) {
            onStartResizing(clientCoordinates, e, rawId);
            break;
          } else if (isRotateHandle(rawId)) {
            onStartRotating(e, rawId);
            break;
          } else if (isDimensionHandle(rawId)) {
            onStartDimensionDrag(clientCoordinates, rawId);
            break;
          } else if (isEditTextHandle(rawId)) {
            const elementId = rawId.split('-edit-text')[0];
            const element = appState.elements[elementId];
            if (element) {
              onStartTextEditing(element);
              setIsMouseDown(false);
            }
            break;
          }

          const elements = onElementClick(rawId, isShiftPressed);
          if (!elements) {
            resetSelection();
            break;
          }
          setupMoving(e, elements.map(copy));
          break;
        }

        // Clicked something outside the currently open group.
        if (activeGroupId) exitGroupEditing(activeGroupId);
        handleNoneModeClick();
        break;
      }
    }
  };

  const onMouseMove: MouseEventHandler<SVGSVGElement> = (e) => {
    const clientCoordinates = getClientCoordinates(e, viewBox);
    const { movementX, movementY } = e;

    setCursorPosition(clientCoordinates);

    const isEphemeral = true;

    if (e.button === MouseButtons.MIDDLE) {
      e.preventDefault();
      onPanning(movementX, movementY);
    }
    if (e.button !== MouseButtons.LEFT) return;

    switch (selectionMode.type) {
      case SelectionModes.QuickPanning:
      case SelectionModes.Panning: {
        onPanning(movementX, movementY);
        break;
      }
      case SelectionModes.MultiSelecting: {
        onMultiSelecting(clientCoordinates);
        break;
      }
      case SelectionModes.Selected:
      case SelectionModes.GroupEditing: {
        // selectionMode can become Selected/GroupEditing without a fresh
        // mousedown (e.g. entering GroupEditing via double-click, or
        // Escape/click-outside reselecting the group) — guard against
        // replaying stale selectionCoordinates (from a previous drag) on a
        // plain hover with no button held.
        if (isMouseDown) onSelected(e);
        break;
      }
      case SelectionModes.Add: {
        onUpdateAddElement(clientCoordinates, isEphemeral);
        break;
      }
      case SelectionModes.Resizing: {
        e.preventDefault();
        onResizingElement(clientCoordinates, isEphemeral);
        break;
      }
      case SelectionModes.Rotating: {
        onRotating(clientCoordinates, isEphemeral);
        break;
      }
      case SelectionModes.DimensionDragging: {
        onDimensionDragging(clientCoordinates, isEphemeral);
        break;
      }
      case SelectionModes.ApexDragging: {
        e.preventDefault();
        onApexDragging(clientCoordinates, isEphemeral);
        break;
      }
      case SelectionModes.RadiusDragging: {
        e.preventDefault();
        onRadiusDragging(clientCoordinates, isEphemeral);
        break;
      }
    }
  };

  const onMouseUp: MouseEventHandler<SVGSVGElement> = (e) => {
    setIsMouseDown(false);
    clearSnapping();
    const isEphemeral = false;

    if (e.button === MouseButtons.MIDDLE) {
      resetSelection();
    }
    if (e.button !== MouseButtons.LEFT) return;
    const selectedElement = selectedElements[0];

    switch (selectionMode.type) {
      case SelectionModes.Selected: {
        if (!history) return;
        setSelectionMode({
          ...selectionMode,
          type: SelectionModes.None,
        });
        onStopSelection();
        break;
      }
      case SelectionModes.GroupEditing: {
        if (!history) return;
        // Stay in GroupEditing after committing a child move (unlike
        // Selected, which drops back to None).
        onStopSelection();
        break;
      }
      case SelectionModes.Add: {
        if (!selectedElement || !history) return;
        const element = appState.elements[selectedElement];
        if (!element) return;
        if (element.type === ElementType.Polyline) {
          break;
        }

        onAddFinishElement();

        setSelectionMode({
          ...selectionMode,
          type: SelectionModes.None,
        });
        break;
      }
      case SelectionModes.Rotating:
      case SelectionModes.Resizing: {
        const isMultiselect = selectionCoordinates.originElements.length > 1;

        if (isMultiselect) {
          const updatedElements = selectionCoordinates.originElements
            .map((origEl) => {
              const current = appState.elements[origEl.id];
              return current
                ? { ...copy(current), state: ElementState.Visible }
                : null;
            })
            .filter((e): e is NonNullable<typeof e> => e !== null);

          const command = new UpdateCommand(
            updatedElements,
            selectionCoordinates.originElements,
            session.currentUserId,
            isEphemeral
          );
          commandManager.executeCommand(command);
          setSelectionMode({ ...selectionMode, type: SelectionModes.None });
          break;
        }

        if (!selectedElement || !history) return;

        const element = copy(appState.elements[selectedElement]);
        if (!element) return;
        const newElement = { ...element, state: ElementState.Visible };

        const command = new UpdateCommand(
          [newElement],
          [element],
          session.currentUserId,
          isEphemeral
        );
        commandManager.executeCommand(command);

        setSelectionMode({
          ...selectionMode,
          type: SelectionModes.None,
        });
        break;
      }
      case SelectionModes.QuickPanning: {
        resetSelection();
        break;
      }
      case SelectionModes.MultiSelecting: {
        if (selectedElements.length === 0) {
          resetSelection();
        }
        resetMultiSelection();
        break;
      }
      case SelectionModes.DimensionDragging: {
        const clientCoordinates = getClientCoordinates(e, viewBox);
        onDimensionDragging(clientCoordinates, isEphemeral);
        onStopDimensionDrag();
        break;
      }
      case SelectionModes.ApexDragging: {
        const clientCoordinates = getClientCoordinates(e, viewBox);
        onApexDragging(clientCoordinates, isEphemeral);
        onStopApexDrag();
        break;
      }
      case SelectionModes.RadiusDragging: {
        const clientCoordinates = getClientCoordinates(e, viewBox);
        onRadiusDragging(clientCoordinates, isEphemeral);
        onStopRadiusDrag();
        break;
      }
    }
  };

  return (
    <MouseEventsContext.Provider
      value={{
        onMouseOver,
        onMouseDown,
        onMouseMove,
        onMouseUp,
      }}
    >
      {children}
    </MouseEventsContext.Provider>
  );
};
